import React, { FC, useEffect, useState } from "react";

import { StepBar } from "@/components/molecules";
import { Banner } from "@/components/molecules/Feedback";
import { FooterFlowButtons, WizardStep } from "@/components/organisms";
import { NavigationButtonTranslations } from "@/components/organisms/FooterFlowButtons/FooterFlowButtons";

import { StyledContainer, StyledOuterContainer, StyledStepBarContainer } from "./styled";

export type WizardProps = {
    wizardStep: WizardStep;
    wizardSteps: WizardStep[];
    onStep: (activeStep: WizardStep) => void;
    canContinue: () => Promise<boolean>;
    onFinish: () => void;
    blockNavigation?: boolean;
    disableNavigation?: boolean;
    hideStepBar?: boolean;
    navigationLabels?: NavigationButtonTranslations;
    bannerContent?: string;
    condensed?: boolean;
};

const Wizard: FC<WizardProps> = ({
    wizardStep,
    wizardSteps,
    onStep,
    canContinue,
    onFinish,
    blockNavigation = false,
    disableNavigation = false,
    hideStepBar = false,
    bannerContent = null,
    navigationLabels,
    condensed = false,
}) => {
    const [hideNavigation, setHideNavigation] = useState(wizardStep.hideNavigation);

    const getStepIndex = (stepId: number) => wizardSteps.findIndex((step) => step.stepId === stepId);
    const getStepBarIndex = (stepBarId: number) => wizardSteps.findIndex((step) => step.stepBarId === stepBarId);

    const steps = wizardSteps
        .filter((step, index) => getStepBarIndex(step.stepBarId) === index)
        .map((step) => ({ label: step.title, stepId: step.stepBarId }));

    const changeActiveStep = async (nextStepIndex: number) => {
        const nextWizardStep = wizardSteps[nextStepIndex];

        const goesFurther = getStepIndex(wizardStep.stepId) < nextStepIndex;

        if (goesFurther) {
            const result = await canContinue();
            if (!result) return;
        }

        if (nextWizardStep) {
            onStep(nextWizardStep);
        } else {
            onFinish();
        }
    };

    const changeActiveStepByStepBarId = async (nextStepBarId: number) => {
        const nextWizardStepIndex = getStepBarIndex(nextStepBarId);
        await changeActiveStep(nextWizardStepIndex);
    };

    const changeActiveStepByStepId = async (nextStepId: number) => {
        const nextWizardStepIndex = getStepIndex(nextStepId);
        await changeActiveStep(nextWizardStepIndex);
    };

    const onPrevious = () => changeActiveStep(getStepIndex(wizardStep.stepId) - 1);
    const onNext = () => changeActiveStep(getStepIndex(wizardStep.stepId) + 1);

    useEffect(() => {
        setHideNavigation(wizardStep.hideNavigation);
    }, [wizardStep]);

    return (
        <>
            {!hideStepBar && (
                /**
                    TODO: Fix vertical spacing according to the current design specs:
                    mobile: https://www.figma.com/file/vW7I3XLKNezVSXoImrC14B/MB%2FWeb%2FLegal-Forms?node-id=5768-43957&mode=dev
                    desktop: https://www.figma.com/file/vW7I3XLKNezVSXoImrC14B/MB%2FWeb%2FLegal-Forms?node-id=7299-21492&mode=dev
                */
                <StyledStepBarContainer style={{ marginTop: "24px" }}>
                    <StepBar currentStep={wizardStep.stepBarId} steps={steps} onClick={changeActiveStepByStepBarId} />
                </StyledStepBarContainer>
            )}

            <StyledOuterContainer>
                <StyledContainer $condensed={condensed}>
                    <wizardStep.component
                        changeActiveStep={changeActiveStep}
                        changeActiveStepByStepId={changeActiveStepByStepId}
                        activeStep={wizardStep.stepId}
                        onFinish={onFinish}
                        onPrevious={onPrevious}
                        setHideNavigation={setHideNavigation}
                    />

                    {!hideNavigation && (
                        /**
                            TODO: Fix vertical spacing according to the current design specs:
                            mobile: https://www.figma.com/file/vW7I3XLKNezVSXoImrC14B/MB%2FWeb%2FLegal-Forms?node-id=5774-44154&mode=dev
                            desktop: https://www.figma.com/file/vW7I3XLKNezVSXoImrC14B/MB%2FWeb%2FLegal-Forms?node-id=5766-46279&mode=dev
                        */
                        <FooterFlowButtons
                            disableNext={blockNavigation}
                            disableNavigation={disableNavigation}
                            onNext={onNext}
                            onBack={onPrevious}
                            translations={navigationLabels}
                            canBack={wizardStep.stepId - 1 >= 0}
                        />
                    )}
                </StyledContainer>
            </StyledOuterContainer>

            {bannerContent && wizardStep.displayBanner && (
                <Banner type="info" fullWidth>
                    {bannerContent}
                </Banner>
            )}
        </>
    );
};
export default Wizard;
